import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {NotifyModalData} from '@/screens/Processor/Metrics/NotifyModal';
import type {Metrics, MetricsData} from '@/screens/Processor/Metrics/types';
import type {NameResolution} from '@/screens/Processor/Resolvers/Cache/types';
import type {AfriexTestTransaction} from '@/screens/Processor/TestTransactions/types';
import type {
  AdminPaginatedEnvelope,
  AfriexCorridorCost,
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexProcessor,
  AfriexQueryParams,
  AfriexResolverProvider,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  SupportedAssets,
} from '@/types';
import {AfriexTransactionType} from '@/types';

const commonEndpoint = '/v2/admin/processors';
const commonMgtEndpoint = '/v2/admin/processor';
const commonCodeMgtEndpoint = '/v2/admin/processor/code';

export type ResolverFamily = 'bank' | 'mobile' | 'ach' | 'card';

export type ResolverProviderMap = Record<
  ResolverFamily,
  AfriexResolverProvider[]
>;

/**
 * The registered providers narrowed to the currencies each one can serve.
 *
 * The sibling of `ResolverProviderMap`, and the screen needs both: an empty
 * entry here with a non-empty list there means "adapters exist for this family,
 * none of them serves this currency", which is what the disabled field has to
 * say. Arrays rather than the corridor matrix's comma-joined string because the
 * modal's job is to intersect the two, and that is one filter with no parsing.
 */
type ResolverFamilyMatrix = Record<
  ResolverFamily,
  Partial<Record<AfriexCurrencyCode, AfriexResolverProvider[]>>
>;

/**
 * `dev` and `retired` list the providers whose node would serve a currency
 * but is switched off by the server (not live in production, or retired).
 */
export type ResolverCapabilityMatrix = ResolverFamilyMatrix & {
  dev?: Partial<ResolverFamilyMatrix>;
  retired?: Partial<ResolverFamilyMatrix>;
};

export type CorridorProcessors = Partial<
  Record<
    AfriexTransactionType,
    Partial<
      Record<
        AfriexTransactionChannel,
        Partial<Record<AfriexCurrencyCode, string>>
      >
    >
  >
>;

/**
 * Processors that can serve each corridor, plus those declared for it but
 * switched off by the server: `dev` (not live in production) and `retired`.
 */
export type ProcessorCapabilityMatrix = CorridorProcessors & {
  dev?: CorridorProcessors;
  retired?: CorridorProcessors;
};

export type DownTimeNotificationRequest = {
  fromCurrency?: AfriexCurrencyCode;
  toCurrency?: AfriexCurrencyCode;
  transactionType: AfriexTransactionType;
  paymentInstrument?: string;
  processorName: AfriexTransactionProcessor;
  expiresAt: number; // unix timestamp
  message: string;
};

// One queryable balance slot for a processor. For most rails it's just a
// currency (one row per supportedAssets entry). Processors whose treasury is
// segmented by an extra dimension (Utila per network, Cellulant per provider,
// Stripe per connected-account country) emit one row per `(currency, params)`
// slot. The label is server-supplied so it can match how the processor itself
// names the slot, e.g. "USDC · SOLANA_MAINNET", "KES · SAFARICOM".
export type ProcessorBalanceQuery = {
  currency: SupportedAssets;
  label: string;
  params?: {
    network?: string;
    provider?: string;
    userCountryOrPaymentMethodCountry?: AfriexCountryCode;
  };
};

// Result of running one ProcessorBalanceQuery. `balance` is a number on
// success, or a string sentinel ("error" / "balance check not supported" /
// "disabled") when the call failed, the node doesn't implement
// getBalanceByCurrency, or the server skipped a processor that has every
// capability switched off in its environment.
export type ProcessorBalanceRow = ProcessorBalanceQuery & {
  balance: number | string;
};

export type ProcessorBalanceDetail = {
  rows: ProcessorBalanceRow[];
};

export type ProcessorBalancesResponse = Partial<
  Record<AfriexTransactionProcessor, ProcessorBalanceDetail>
>;

export const processorService = {
  getProcessorMatrix: async (): Promise<ProcessorCapabilityMatrix> => {
    const response = await axios.get<ProcessorCapabilityMatrix>(
      `${commonEndpoint}/capability-matrix`,
    );
    return response.data;
  },
  getProcessorAll: async (): Promise<AfriexTransactionProcessor[]> => {
    const response =
      await axios.get<AfriexTransactionProcessor[]>(commonEndpoint);
    return response.data;
  },
  /**
   * The processors each `params.resolvers` family can actually dispatch to.
   *
   * Deliberately not `getProcessorAll`: only a subset of processors has a
   * name-resolver adapter registered, and the currency PATCH schema accepts
   * `params.resolvers` as an unvalidated object, so offering the full list
   * lets an operator save a value that fails every lookup at request time.
   */
  getResolverProviders: async (): Promise<ResolverProviderMap> => {
    const response = await axios.get<ResolverProviderMap>(
      `${commonEndpoint}/resolver-providers`,
    );
    return response.data;
  },
  /**
   * Which of those providers can actually serve each currency.
   *
   * Same reason `getResolverProviders` exists, one step further: a provider
   * with an adapter can still resolve nothing for the currency an operator
   * picked it for, and `patchCurrencySchema` accepts `params.resolvers` as an
   * unvalidated object, so the failure is silent until someone transacts.
   */
  getResolverMatrix: async (): Promise<ResolverCapabilityMatrix> => {
    const response = await axios.get<ResolverCapabilityMatrix>(
      `${commonEndpoint}/resolver-matrix`,
    );
    return response.data;
  },
  /**
   * Cached account-name lookups, most recently resolved first. A mobile search
   * needs `country`: the server normalizes the phone to the E.164 key it was
   * cached under.
   */
  getNameResolutions: async ({
    page = 0,
    limit = 20,
    ...filters
  }: {
    page?: number;
    limit?: number;
    channel?: string;
    country?: string;
    resolvedBy?: string;
    status?: string;
    search?: string;
  }): Promise<AdminPaginatedEnvelope<NameResolution[]>> => {
    // toQueryString emits an undefined value as the string "undefined", so
    // unset filters are left out rather than passed through.
    const setFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value),
    );
    const response = await axios.get<AdminPaginatedEnvelope<NameResolution[]>>(
      `${commonEndpoint}/name-resolutions?${toQueryString({page, limit, ...setFilters})}`,
    );
    return response.data;
  },
  /** Processor test runs, newest first. */
  getTestTransactions: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexTestTransaction[]>> => {
    const response = await axios.get<
      AdminPaginatedEnvelope<AfriexTestTransaction[]>
    >(
      `${commonEndpoint}/test-transactions?${toQueryString({page: 0, limit: 10, ...filters})}`,
    );
    return response.data;
  },
  /** Retire one cached lookup so the next lookup for that account goes live. */
  invalidateNameResolution: async (id: string): Promise<NameResolution> => {
    const response = await axios.post<NameResolution>(
      `${commonEndpoint}/name-resolutions/${id}/invalidate`,
    );
    return response.data;
  },
  createDowntimeNotification: async (
    body: NotifyModalData,
  ): Promise<unknown> => {
    let expiresAt: number;
    switch (body.ttl) {
      case '2hrs':
        expiresAt = Date.now() + 2 * 60 * 60 * 1000;
        break;
      case '1hr':
        expiresAt = Date.now() + 60 * 60 * 1000;
        break;
      case '30mins':
        expiresAt = Date.now() + 30 * 60 * 1000;
        break;
      default: // 1hr
        expiresAt = Date.now() + 60 * 60 * 1000;
    }
    // slice off last 3 chars to get currency code
    const toCurrency = body.selectedCorridorPair
      ?.slice(-3)
      .toUpperCase() as AfriexCurrencyCode;
    const fromCurrency = body.selectedCorridorPair
      ?.slice(0, 3)
      .toUpperCase() as AfriexCurrencyCode;

    const {message} = body;
    const request: DownTimeNotificationRequest = {
      fromCurrency,
      toCurrency,
      transactionType: AfriexTransactionType.WITHDRAW,
      paymentInstrument: body.selectedPaymentInstrument,
      processorName: body.selectedProcessor as AfriexTransactionProcessor,
      expiresAt,
      message,
    };

    const response = await axios.post<unknown>(
      `${commonEndpoint}/downtime`,
      request,
    );
    return response.data;
  },

  getProcessorMetrics: async (): Promise<{
    data: MetricsData[];
    metrics: Metrics;
  }> => {
    const response = await axios.get<{data: MetricsData[]; metrics: Metrics}>(
      `${commonEndpoint}/metrics`,
    );
    return response.data;
  },

  getProcessorBalance: async (): Promise<ProcessorBalancesResponse> => {
    const response = await axios.get<ProcessorBalancesResponse>(
      `${commonEndpoint}/balance`,
    );
    return response.data;
  },

  getProcessorBalanceDenomination: async (): Promise<
    Record<
      string,
      | 'dollar-denominated-global'
      | 'local-denominated'
      | 'dollar-denominated-local'
    >
  > => {
    const response = await axios.get<
      Record<
        string,
        | 'dollar-denominated-global'
        | 'local-denominated'
        | 'dollar-denominated-local'
      >
    >(`${commonEndpoint}/balance/denomination`);
    return response.data;
  },

  getProcessorList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexProcessor[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `${commonMgtEndpoint}?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexProcessor[]>>(url);
    return response.data;
  },

  updateProcessorById: async (
    id: string,
    body: Partial<AfriexProcessor>,
  ): Promise<AfriexProcessor> => {
    const response = await axios.patch<AfriexProcessor>(
      `${commonMgtEndpoint}/${id}`,
      body,
    );
    return response.data;
  },

  createProcessorCost: async (
    processorCode: AfriexTransactionProcessor,
    body: Partial<AfriexCorridorCost>,
  ): Promise<AfriexCorridorCost> => {
    const response = await axios.post<AfriexCorridorCost>(
      `${commonCodeMgtEndpoint}/${processorCode}/costs`,
      body,
    );
    return response.data;
  },

  updateProcessorCost: async (
    processorCode: AfriexTransactionProcessor,
    costId: string,
    body: Partial<AfriexCorridorCost>,
  ): Promise<AfriexCorridorCost> => {
    const response = await axios.patch<AfriexCorridorCost>(
      `${commonCodeMgtEndpoint}/${processorCode}/costs/${costId}`,
      body,
    );
    return response.data;
  },
};
